<template>
  <TabGroup
    as="div"
    :selected-index="selectedIndex"
    @change="handleChange"
  >
    <div class="tw-bg-gray-50 tw-border-b tw-border-gray-300 tw-px-4 tw-flex tw-items-center tw-justify-between tw-gap-4">
      <TabList
        class="tw--mb-px tw-flex tw-space-x-8"
        :aria-label="ariaLabel"
      >
        <Tab
          v-for="(tab, i) in tabMeta"
          :key="i"
          v-slot="{ selected }"
          :disabled="tab.disabled"
          as="template"
        >
          <button
            type="button"
            :class="[
              selected
                ? 'tw-border-primary-600 tw-text-primary-600'
                : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700 hover:tw-border-gray-300',
              tab.disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer',
              'tw-whitespace-nowrap tw-py-4 tw-px-1 tw-appearance-none tw-border-0 tw-border-b-2 tw-font-medium tw-text-sm tw-flex tw-items-center tw-gap-2 tw-bg-transparent focus:tw-outline-none focus-visible:tw-outline-none'
            ]"
          >
            <i
              v-if="tab.icon"
              :class="tab.icon"
              aria-hidden="true"
            />
            {{ tab.title }}
          </button>
        </Tab>
      </TabList>
      <!-- Actions area: rendered to the right of the tab list. Children can
           teleport here via the injected `uiTabsActionsTarget` (e.g. a
           PagedTable's search input). The named slot is the public API for
           call-sites that want to render actions directly. -->
      <div ref="actionsTarget" class="ui-tabs-actions tw-flex tw-items-center tw-gap-2">
        <slot name="actions" />
      </div>
    </div>
    <TabPanels
      as="div"
      class="tw-mt-4 tw-bg-white tw-rounded-lg tw-shadow tw-p-6"
    >
      <slot />
    </TabPanels>
  </TabGroup>
</template>

<script setup lang="ts">
  import { ref, computed, provide, useSlots, Fragment, Comment } from 'vue'
  import type { VNode } from 'vue'
  import { TabGroup, TabList, Tab, TabPanels } from '@headlessui/vue'
  import UiTab from './UiTab.vue'

  defineOptions({ name: 'UiTabs' })

  // Teleport target for child components that want to render into the
  // actions area (right side of the tab strip). PagedTable uses this to
  // hoist its search input out of the panel and up next to the tabs.
  const actionsTarget = ref<HTMLElement | null>(null)
  provide('uiTabsActionsTarget', actionsTarget)

  interface Props {
    modelValue?: number
    ariaLabel?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: undefined,
    ariaLabel: 'Tabs'
  })

  const emit = defineEmits<{
    'update:modelValue': [index: number]
    change: [index: number]
  }>()

  // Vue treats `<UiTab active>` (boolean shorthand) as props.active === ''.
  const truthy = (v: unknown) => v === true || v === ''

  // Walk slot VNodes recursively, flattening Fragments and discarding comments
  // (so v-if'd siblings don't pollute the tab list).
  function flattenVNodes (vnodes: VNode[]): VNode[] {
    const out: VNode[] = []
    for (const vn of vnodes) {
      if (vn.type === Fragment) {
        out.push(...flattenVNodes((vn.children as VNode[]) ?? []))
      } else if (vn.type === Comment) {
        // skip
      } else {
        out.push(vn)
      }
    }
    return out
  }

  const slots = useSlots()

  // Derive tab metadata synchronously from the default slot VNodes. This means
  // by the time TabGroup first renders we already know every tab's title /
  // active / lazy / disabled — no provide/inject roundtrip, no first-paint
  // mismatch where TabGroup mounts with zero tabs.
  const tabMeta = computed(() => {
    const vnodes = flattenVNodes(slots.default?.() ?? [])
    return vnodes
      .filter(vn => {
        const t = vn.type as { name?: string } | typeof UiTab
        return t === UiTab || (typeof t === 'object' && (t as { name?: string }).name === 'UiTab')
      })
      .map(vn => ({
        title: (vn.props?.title as string) ?? '',
        icon: (vn.props?.icon as string) ?? '',
        active: truthy(vn.props?.active),
        lazy: truthy(vn.props?.lazy),
        disabled: truthy(vn.props?.disabled)
      }))
  })

  // Internal selection state. Null until the user (or v-model) overrides the
  // default — this lets the default track tabMeta reactively even after a
  // v-if'd tab is added or removed.
  const userIndex = ref<number | null>(null)

  const defaultIndex = computed(() => {
    const idx = tabMeta.value.findIndex(t => t.active)
    return idx >= 0 ? idx : 0
  })

  const selectedIndex = computed(() => {
    if (props.modelValue !== undefined) return props.modelValue
    return userIndex.value ?? defaultIndex.value
  })

  function handleChange (index: number) {
    userIndex.value = index
    emit('update:modelValue', index)
    emit('change', index)
  }
</script>

<style>
  /* Suppress the UA focus ring on tab buttons — the underline + primary text
     colour is the visual focus indicator. Not scoped so the rule reliably
     reaches the button regardless of how Headless UI mounts it. The
     [role='tablist'] ancestor selector keeps it from leaking to other buttons. */
  [role='tablist'] button:focus,
  [role='tablist'] button:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
</style>
